import { useScroll } from '@use-gesture/react';
import { createRef, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import twMerge from 'twMerge';

import Icon from 'molecules/Icon';
import Image from 'molecules/Image';

import {
  switchMobileDropdownContainer,
  switchMobileDropdownItem,
  switchTabHeader,
  switchTabIcon,
  switchTabMobileHeader,
  switcherContainer,
  switcherContent,
  switcherItemContent,
  switcherTabArrowContainer,
  switcherTabItem,
  switcherTabScrollContainer,
} from 'components/OffsetSwitcher/components/v2/index.styles';

import onKeyDown from 'utils/onKeyDown';
import richTextParser from 'utils/richTextParser/richTextParser';

import screens from 'theme/spacing/screens';

import type { ContentfulItemOffsetSwitcher } from 'graphqlTypes';
import type { DropdownItemProps } from 'molecules/Dropdown/DropdownItem';
import type { ImageData } from 'molecules/Image';
import type { backgroundType } from 'molecules/Section';
import type { FC } from 'react';
import type { RichText } from 'utils/richTextParser/richTextParser';

import 'components/OffsetSwitcher/components/v2/styles.css';

export type OffsetSwitcherV2Props = {
  references?: Array<ContentfulItemOffsetSwitcher | null> | null;
  background?: backgroundType;
  theme?: 'light' | 'dark';
};

const OffsetSwitcherV2: FC<OffsetSwitcherV2Props> = ({ references, background, theme }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const isMobile = useMedia(`(max-width: ${screens.lg})`, false);
  const scrollRef = useRef(null);
  const dropdownRef = useRef(null);
  const linkRefs = references ? references?.map(() => createRef<HTMLAnchorElement>()) : [];
  const TabItem = 'div';
  const MobileSelect = 'div';
  const MobileSelectItem = 'div';
  const TabScrollArrow = 'div';
  const listItems: DropdownItemProps[] = [];
  references &&
    references.map(item => {
      listItems.push({ label: item?.label } as DropdownItemProps);

      return;
    });

  const checkTopBottom = () => {
    if (isMobile) {
      return;
    }

    if (!scrollRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const atTop = Math.floor(scrollTop) === 0;
    const atBottom = scrollHeight - Math.ceil(scrollTop) <= clientHeight;
    setIsAtTop(atTop);
    setIsAtBottom(atBottom);
  };

  const handleClickOutside = event => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsMobileDropdownOpen(false);
    }
  };

  useScroll(
    () => {
      checkTopBottom();
    },
    { target: scrollRef },
  );

  useEffect(() => {
    checkTopBottom();

    window.addEventListener('resize', checkTopBottom);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', checkTopBottom);
    };
  }, []);

  useEffect(() => {
    const top = activeIndex < 2 ? 0 : linkRefs[activeIndex]?.current?.offsetTop - scrollRef?.current?.offsetTop;

    if (scrollRef?.current?.scrollTo instanceof Function) {
      scrollRef?.current?.scrollTo({ top, behavior: 'smooth' });
    }
  }, [activeIndex]);

  const handleMobileDropdownItemClick = idx => {
    setActiveIndex(idx);
    setIsMobileDropdownOpen(false);
  };

  const handleMoveToTop = () => {
    scrollRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMoveToDown = () => {
    scrollRef?.current?.scrollTo({ top: scrollRef?.current?.scrollHeight, behavior: 'smooth' });
  };

  if (!references) {
    return <></>;
  }

  return (
    <div className={twMerge(switcherContainer({ isMobile: isMobile ? theme : undefined }))}>
      {!isMobile && (
        <div className={twMerge(switcherTabScrollContainer({ background, isAtTop, isAtBottom, theme }))}>
          {!isAtTop && (
            <TabScrollArrow
              className={twMerge(switcherTabArrowContainer({ background, isTop: true }))}
              onClick={handleMoveToTop}
              onKeyDown={e => onKeyDown(e, () => handleMoveToTop())}
            >
              <Icon icon="chevron-up" size={24} className={`${theme === 'light' ? 'fill-gray-700' : 'fill-white'}`} />
            </TabScrollArrow>
          )}
          <div ref={scrollRef} className="no-scrollbar h-full overflow-y-scroll">
            <div className="flex w-full flex-col gap-[10px] py-px pb-[10px] lg:flex-wrap">
              {references.map((item, idx) => (
                <TabItem
                  key={item?.id}
                  className={twMerge(
                    switcherTabItem({
                      theme,
                      isActive: activeIndex === idx ? theme : undefined,
                    }),
                  )}
                  ref={linkRefs[idx]}
                  tabIndex={0}
                  onClick={() => setActiveIndex(idx)}
                  onKeyDown={e => onKeyDown(e, () => setActiveIndex(idx))}
                >
                  <div className={twMerge(switchTabHeader())}>
                    {item?.icon && (
                      <Image
                        image={item?.icon as ImageData}
                        alt={item?.icon?.title || ''}
                        className={twMerge(switchTabIcon({ theme }))}
                      />
                    )}
                    {item?.label && (
                      <p className={`text-lg font-bold ${theme === 'light' ? 'text-black' : 'text-white'}`}>
                        {item?.label}
                      </p>
                    )}
                  </div>
                  {item?.description && (
                    <div
                      className={`office-switcher-tab-description theme-${theme} flex w-full flex-col items-start ${activeIndex !== idx ? 'hidden' : ''} ${isMobile ? 'p-[10px]' : ''}`}
                    >
                      {richTextParser(item?.description as RichText, undefined, theme)}
                    </div>
                  )}
                </TabItem>
              ))}
            </div>
          </div>
          {!isAtBottom && (
            <TabScrollArrow
              className={twMerge(switcherTabArrowContainer({ background, isBottom: true }))}
              onClick={handleMoveToDown}
              onKeyDown={e => onKeyDown(e, () => handleMoveToDown())}
            >
              <Icon icon="chevron-down" size={24} className={`${theme === 'light' ? 'fill-gray-700' : 'fill-white'}`} />
            </TabScrollArrow>
          )}
        </div>
      )}
      {isMobile && (
        <div ref={dropdownRef} className="relative">
          <MobileSelect
            className={twMerge(switchTabMobileHeader({ theme }))}
            onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
            onKeyDown={e => onKeyDown(e, () => setIsMobileDropdownOpen(!isMobileDropdownOpen))}
          >
            <Image
              image={references[activeIndex]?.icon as ImageData}
              alt={references[activeIndex]?.icon?.title || ''}
              className={twMerge(switchTabIcon({ theme }))}
            />
            <p className={`text-lg font-bold ${theme === 'light' ? 'text-black' : 'text-white'}`}>
              {references[activeIndex]?.label}
            </p>
            <Icon
              icon={`chevron-${isMobileDropdownOpen ? 'up' : 'down'}`}
              size={20}
              className={`${theme === 'light' ? 'fill-primary-600' : 'fill-white'} absolute right-[12px] top-[14px]`}
            />
          </MobileSelect>
          <div className={twMerge(switchMobileDropdownContainer({ theme, isOpen: isMobileDropdownOpen }))}>
            <div className="w-full overflow-x-hidden overflow-y-scroll">
              <div className="flex w-full flex-col gap-[10px]">
                {references.map((item, idx) =>
                  activeIndex !== idx ? (
                    <MobileSelectItem
                      className={twMerge(switchMobileDropdownItem({ theme }))}
                      onClick={() => handleMobileDropdownItemClick(idx)}
                      onKeyDown={e => onKeyDown(e, () => setIsMobileDropdownOpen(!idx))}
                      key={idx}
                    >
                      <Image
                        image={item?.icon as ImageData}
                        alt={item?.icon?.title || ''}
                        className={twMerge(switchTabIcon({ theme }))}
                      />
                      <p className={`text-lg font-bold ${theme === 'light' ? 'text-black' : 'text-white'}`}>
                        {item?.label}
                      </p>
                    </MobileSelectItem>
                  ) : null,
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={twMerge(switcherContent({ theme, isMobile }))}>
        <div className="flex w-full flex-col">
          {references.map((item, idx) => (
            <div key={item?.id} className={twMerge(switcherItemContent({ active: idx === activeIndex }))}>
              {isMobile && item?.description && (
                <div
                  className={`office-switcher-tab-description theme-${theme} mb-[10px] flex w-full flex-col items-start ${activeIndex !== idx ? 'hidden' : ''}`}
                >
                  {richTextParser(item?.description as RichText, undefined, theme)}
                </div>
              )}
              {item?.featuredImage && (
                <Image
                  image={item?.featuredImage as ImageData}
                  alt={item?.featuredImage?.title || ''}
                  className="h-fit w-full"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OffsetSwitcherV2;
